import React from "react";
import PeriodCard from "./PeriodCard";
import { Plus, Settings } from "lucide-react";
import { Card, EmptyState } from "../../components/ui";

const DAY_NAMES = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export default function TimetableGrid({
  timetable = {},
  canManage,
  onEditPeriod,
  onDeletePeriod,
  onAddPeriod,
  highlightDay = null,
  periodsPerDay = null,
}) {
  const days = [1, 2, 3, 4, 5, 6];

  // Determine maximum periods present in timetable data
  let maxDataPeriod = 0;
  Object.values(timetable).forEach((periods) => {
    if (Array.isArray(periods)) {
      periods.forEach((p) => {
        if (p.periodNumber > maxDataPeriod) maxDataPeriod = p.periodNumber;
      });
    }
  });

  // Calculate total rows based strictly on DB setting or existing max data period
  const totalPeriods = periodsPerDay || maxDataPeriod;

  // If periodsPerDay is not set in DB and no periods exist in timetable data, show prompt
  if (!totalPeriods || totalPeriods <= 0) {
    return (
      <Card className="p-8 sm:p-12">
        <EmptyState
          icon={<Settings className="h-8 w-8 text-amber-500" />}
          title="Please define no. of classes/period"
          description="Configure the periods per day in timetable settings to display the schedule grid."
        />
      </Card>
    );
  }

  const periodNumbers = Array.from({ length: totalPeriods }, (_, i) => i + 1);

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-sm">
      <div className="overflow-x-auto">
        <div className="min-w-[840px] grid grid-cols-7 divide-x divide-y divide-neutral-200 dark:divide-neutral-800 border-b border-neutral-200 dark:border-neutral-800">
          {/* Header Row */}
          <div className="p-3 bg-neutral-50/80 text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center justify-center">
            Period / Day
          </div>
          {days.map((day) => {
            const isToday = highlightDay === day;
            return (
              <div
                key={day}
                className={`p-3 text-center text-xs font-bold uppercase tracking-wider ${ isToday ? "bg-amber-100/80 text-amber-900 font-extrabold" : "bg-neutral-50/80 text-neutral-700 dark:text-neutral-200" }`}
              >
                {DAY_NAMES[day]} {isToday && "(Today)"}
              </div>
            );
          })}

          {/* Body Grid */}
          {periodNumbers.map((pNum) => (
            <React.Fragment key={pNum}>
              <div className="p-3 bg-neutral-50/80 text-center font-bold text-xs text-neutral-500 dark:text-neutral-400 flex items-center justify-center">
                P{pNum}
              </div>
              {days.map((day) => {
                const dayPeriods = timetable[day] || [];
                const period = dayPeriods.find((p) => p.periodNumber === pNum);

                return (
                  <div
                    key={`${day}-${pNum}`}
                    className="p-1.5 min-h-[120px] flex flex-col justify-stretch bg-white dark:bg-neutral-900 hover:bg-neutral-50/50 transition-colors"
                  >
                    {period ? (
                      <PeriodCard
                        period={period}
                        canManage={canManage}
                        onEdit={onEditPeriod}
                        onDelete={onDeletePeriod}
                      />
                    ) : canManage ? (
                      <button
                        type="button"
                        onClick={() => onAddPeriod(day, pNum)}
                        className="w-full h-full min-h-[95px] border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 hover:border-neutral-400 hover:bg-neutral-50/80 transition-colors gap-1 group p-2"
                      >
                        <Plus className="h-4 w-4 group-hover:scale-110 transition-transform" />
                        <span className="text-[11px] font-medium">Add</span>
                      </button>
                    ) : (
                      <div className="w-full h-full min-h-[95px] rounded-xl bg-neutral-50/40 border border-neutral-100 dark:border-neutral-800 flex items-center justify-center text-xs text-neutral-300 dark:text-neutral-600 italic">
                        Free
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}